import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, Shelter, ShelterStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const EOC_ROLES = ['DUTY_OFFICER', 'EOC_COMMANDER'];
const SHELTER_STATUSES: ShelterStatus[] = ['CLOSED', 'STANDBY', 'OPEN'];
const DISTRICTS = [
  'Beau Vallon', 'Anse Royale', 'Baie Lazare', 'Anse Boileau', 'Takamaka', 'Port Glaud', 'Cascade', 'Au Cap',
  'Anse Aux Pins', 'English River', 'Mont Buxton', 'Plaisance', 'St Louis', 'Roche Caiman', 'Bel Air', 'Mont Fleuri',
  'Glacis', 'Bel Ombre', 'Anse Etoile', 'Machabee', 'Pointe Larue', 'Grand Anse Mahe', 'Grand Anse Praslin', 'Baie Ste Anne',
];

/** Shelter register — status/occupancy/supplies lifecycle with append-only history (M36). */
export function Shelters() {
  const { persona } = useAuth();
  const isEoc = persona ? persona.roles.some((r) => EOC_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [district, setDistrict] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (district) params.set('district', district);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m36/shelters?${params.toString()}`;
  }, [status, district, appliedQ]);
  const listQ = useApiQuery<Page<Shelter>>(isEoc ? path : null);

  return (
    <div>
      <PageHeader
        title="Shelters"
        subtitle="Register + status/occupancy/supplies updates, with a full append-only history per shelter (M36)."
        actions={
          isEoc ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Register shelter
            </button>
          ) : undefined
        }
      />

      {!isEoc ? <RoleNotice roles={EOC_ROLES} /> : null}
      {isEoc ? (
        <>
          <form
            className="mb-4 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-56" placeholder="Ref or name…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={district} onChange={(e) => setDistrict(e.target.value)}>
              <option value="">All districts</option>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {SHELTER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>

          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No shelters match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Name</th>
                    <th className="table-head">District</th>
                    <th className="table-head">Occupancy</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((s) => (
                    <tr key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(s.id)}>
                      <td className="table-cell font-mono text-xs font-medium">{s.refNo}</td>
                      <td className="table-cell">{s.name}</td>
                      <td className="table-cell text-xs text-slate-500">{s.district}</td>
                      <td className="table-cell tabular-nums">
                        {s.occupancy} / {s.capacity}
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={s.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {selectedId ? (
        <ShelterDetailModal id={selectedId} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ShelterDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<Shelter>(`/api/v1/m36/shelters/${id}`);
  const s = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState<ShelterStatus>('OPEN');
  const [occupancy, setOccupancy] = useState('');
  const [note, setNote] = useState('');

  const submitStatus = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m36/shelters/${id}/status`, {
        status: newStatus,
        occupancy: occupancy !== '' ? Number.parseInt(occupancy, 10) : undefined,
        note: note || undefined,
      });
      q.reload();
      onChanged();
      setNote('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={s ? s.name : 'Shelter'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {s ? (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={s.status} />} />
            <Detail label="Occupancy" value={`${s.occupancy} / ${s.capacity}`} />
            <Detail label="District" value={s.district} />
            <Detail label="Address" value={s.address} />
            <Detail label="Operated by" value={s.operatedBy ?? '—'} />
            <Detail label="Manager contact" value={s.managerContact ?? '—'} />
          </div>
          {Object.keys(s.supplies).length > 0 ? (
            <div>
              <div className="field-label">Supplies</div>
              <p className="text-xs text-slate-600">{Object.entries(s.supplies).map(([k, v]) => `${k}: ${v}`).join(', ')}</p>
            </div>
          ) : null}

          <div className="space-y-2 rounded-lg border border-slate-200 p-3">
            <div className="text-sm font-semibold text-slate-700">Update status</div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Status">
                <select className="field-input" value={newStatus} onChange={(e) => setNewStatus(e.target.value as ShelterStatus)}>
                  {SHELTER_STATUSES.map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={`Occupancy (only valid while OPEN, max ${s.capacity})`}>
                <input className="field-input" type="number" min="0" max={s.capacity} value={occupancy} onChange={(e) => setOccupancy(e.target.value)} placeholder={String(s.occupancy)} />
              </Field>
            </div>
            <Field label="Note (optional)">
              <input className="field-input" value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <button className="btn-primary" disabled={busy} onClick={submitStatus}>
              {busy ? 'Saving…' : 'Record update'}
            </button>
          </div>

          <div>
            <div className="mb-2 text-sm font-semibold text-slate-700">History</div>
            {(s.updates?.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-400">No status updates recorded yet.</p>
            ) : (
              <div className="max-h-56 space-y-1.5 overflow-y-auto">
                {s.updates!.map((u) => (
                  <div key={u.id} className="flex items-center justify-between rounded border border-slate-100 px-2.5 py-1.5 text-xs">
                    <span>
                      <StatusBadge status={u.status} /> occupancy {u.occupancy}
                      {u.note ? ` — ${u.note}` : ''}
                    </span>
                    <span className="text-slate-400">{formatDateTime(u.createdAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [district, setDistrict] = useState(DISTRICTS[0]!);
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [capacity, setCapacity] = useState('');
  const [operatedBy, setOperatedBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flags, setFlags] = useState<string[] | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<Shelter & { siteRiskFlags?: string[] }>('/api/v1/m36/shelters', {
        name,
        district,
        address,
        lat: lat ? Number.parseFloat(lat) : undefined,
        lng: lng ? Number.parseFloat(lng) : undefined,
        capacity: Number.parseInt(capacity, 10),
        operatedBy: operatedBy || undefined,
      });
      if (created.siteRiskFlags && created.siteRiskFlags.length > 0) {
        setFlags(created.siteRiskFlags);
        return;
      }
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register shelter');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register shelter" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {flags ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Advisory: this site overlaps a real C9 hazard layer — {flags.join(', ')}. Registration proceeded (this is an advisory check, not a gate).
            </div>
            <button className="btn-primary" onClick={onDone}>
              Continue
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="District">
                <select className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)}>
                  {DISTRICTS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Address">
              <input className="field-input" value={address} onChange={(e) => setAddress(e.target.value)} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Latitude (optional)">
                <input className="field-input" type="number" step="0.000001" value={lat} onChange={(e) => setLat(e.target.value)} />
              </Field>
              <Field label="Longitude (optional)">
                <input className="field-input" type="number" step="0.000001" value={lng} onChange={(e) => setLng(e.target.value)} />
              </Field>
              <Field label="Capacity">
                <input className="field-input" type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
              </Field>
            </div>
            <Field label="Operated by (optional)">
              <input className="field-input" value={operatedBy} onChange={(e) => setOperatedBy(e.target.value)} placeholder="e.g. Seychelles Red Cross" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button className="btn-primary" disabled={busy || !name || !address || !capacity} onClick={submit}>
                {busy ? 'Registering…' : 'Register shelter'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
